'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
interface MonthlyData {
  month: string;
  year: number;
  avgSoldPrice: number | null;
  avgSoldPricePerSqFt: number | null;
  salesCount: number;
}
interface SoldPriceChartProps {
  monthlyData: MonthlyData[];
  yearlyAvgSoldPrice: number | null;
  yearlyAvgSoldPricePerSqFt: number | null;
  variant?: 'classic' | 'luxury';
}
function formatPrice(price: number): string {
  if (price >= 1000000) {
    return `$${(price / 1000000).toFixed(1)}M`;
  }
  if (price >= 1000) {
    return `$${Math.round(price / 1000)}K`;
  }
  return `$${price.toLocaleString()}`;
}
export default function SoldPriceChart(t0) {
  const $ = _c(59);
  const {
    monthlyData,
    yearlyAvgSoldPrice,
    yearlyAvgSoldPricePerSqFt,
    variant: t1
  } = t0;
  t1 === undefined ? "classic" : t1;
  let maxPrice;
  let maxPsf;
  if ($[0] !== monthlyData) {
    maxPrice = 0;
    maxPsf = 0;
    monthlyData.forEach(d => {
      if (d.avgSoldPrice && d.avgSoldPrice > maxPrice) {
        maxPrice = d.avgSoldPrice;
      }
      if (d.avgSoldPricePerSqFt && d.avgSoldPricePerSqFt > maxPsf) {
        maxPsf = d.avgSoldPricePerSqFt;
      }
    });
    $[0] = monthlyData;
    $[1] = maxPrice;
    $[2] = maxPsf;
  } else {
    maxPrice = $[1];
    maxPsf = $[2];
  }
  const t2 = maxPrice * 1.2;
  const t3 = maxPsf * 1.2;
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = {
      maxSoldPrice: t2,
      maxPricePerSqFt: t3
    };
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    maxSoldPrice,
    maxPricePerSqFt
  } = t4;
  const getBarHeight = _temp;
  const getBaselinePosition = _temp2;
  const hasData = monthlyData.some(_temp3);
  if (!hasData) {
    let t5;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="bg-white dark:bg-[#1a1a1a] border border-[#e8e6e3] dark:border-gray-800 p-8"><p className="text-center text-sm text-[#6a6a6a] dark:text-gray-400 font-light">No sold data available for the selected period</p></div>;
      $[6] = t5;
    } else {
      t5 = $[6];
    }
    return t5;
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="p-6 border-b border-[#e8e6e3] dark:border-gray-800"><div className="flex items-center gap-3"><div className="w-8 h-px bg-[var(--color-gold)]" /><span className="text-[10px] uppercase tracking-[0.2em] text-[#8a8a8a] font-light">Sales Trends</span></div></div>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <h4 className="text-xs uppercase tracking-[0.15em] text-[#6a6a6a] dark:text-gray-400 font-light mb-6 text-center">Avg. Sold Price by Month</h4>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">{[0, 1, 2].map(_temp4)}</div>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== maxSoldPrice || $[11] !== yearlyAvgSoldPrice) {
    t8 = yearlyAvgSoldPrice && maxSoldPrice > 0 && <div className="absolute left-0 right-0 flex items-center z-10" style={{
      bottom: `${getBaselinePosition(yearlyAvgSoldPrice, maxSoldPrice)}%`
    }}><div className="h-px flex-1 bg-[var(--color-gold)]" style={{
        backgroundImage: "repeating-linear-gradient(90deg, var(--color-gold) 0, var(--color-gold) 4px, transparent 4px, transparent 8px)"
      }} /><span className="ml-2 text-[9px] uppercase tracking-[0.1em] text-[var(--color-gold)] font-light whitespace-nowrap">{formatPrice(yearlyAvgSoldPrice)}</span></div>;
    $[10] = maxSoldPrice;
    $[11] = yearlyAvgSoldPrice;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== maxSoldPrice || $[14] !== monthlyData) {
    let t10;
    if ($[16] !== maxSoldPrice) {
      t10 = (d_1, i_0) => <div key={i_0} className="flex-1 flex flex-col items-center h-full justify-end group">{d_1.avgSoldPrice && <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 mb-1 text-[10px] text-[#1a1a1a] dark:text-white font-light bg-white dark:bg-[#2a2a2a] px-1.5 py-0.5 border border-[#e8e6e3] dark:border-gray-700 shadow-sm">{formatPrice(d_1.avgSoldPrice)}</div>}<div className="w-full max-w-[24px] bg-[var(--color-navy)] dark:bg-[var(--color-gold)] transition-all duration-300 hover:opacity-80" style={{
          height: `${getBarHeight(d_1.avgSoldPrice, maxSoldPrice)}%`
        }} /></div>;
      $[16] = maxSoldPrice;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    t9 = monthlyData.map(t10);
    $[13] = maxSoldPrice;
    $[14] = monthlyData;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = <div className="flex items-end justify-between h-full gap-1 relative z-0">{t9}</div>;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t8) {
    t11 = <div className="relative h-32">{t7}{t8}{t10}</div>;
    $[20] = t10;
    $[21] = t8;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== monthlyData) {
    t12 = monthlyData.map(_temp5);
    $[23] = monthlyData;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12) {
    t13 = <div className="flex justify-between mt-3 px-0.5">{t12}</div>;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t11 || $[28] !== t13) {
    t14 = <div className="p-6 md:p-8 md:border-r border-b md:border-b-0 border-[#e8e6e3] dark:border-gray-800">{t6}{t11}{t13}</div>;
    $[27] = t11;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <h4 className="text-xs uppercase tracking-[0.15em] text-[#6a6a6a] dark:text-gray-400 font-light mb-6 text-center">Avg. Sold $/Sq Ft by Month</h4>;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">{[0, 1, 2].map(_temp6)}</div>;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== maxPricePerSqFt || $[33] !== yearlyAvgSoldPricePerSqFt) {
    t17 = yearlyAvgSoldPricePerSqFt && maxPricePerSqFt > 0 && <div className="absolute left-0 right-0 flex items-center z-10" style={{
      bottom: `${getBaselinePosition(yearlyAvgSoldPricePerSqFt, maxPricePerSqFt)}%`
    }}><div className="h-px flex-1 bg-[var(--color-gold)]" style={{
        backgroundImage: "repeating-linear-gradient(90deg, var(--color-gold) 0, var(--color-gold) 4px, transparent 4px, transparent 8px)"
      }} /><span className="ml-2 text-[9px] uppercase tracking-[0.1em] text-[var(--color-gold)] font-light whitespace-nowrap">${yearlyAvgSoldPricePerSqFt}</span></div>;
    $[32] = maxPricePerSqFt;
    $[33] = yearlyAvgSoldPricePerSqFt;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== maxPricePerSqFt || $[36] !== monthlyData) {
    let t19;
    if ($[38] !== maxPricePerSqFt) {
      t19 = (d_3, i_3) => <div key={i_3} className="flex-1 flex flex-col items-center h-full justify-end group">{d_3.avgSoldPricePerSqFt && <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 mb-1 text-[10px] text-[#1a1a1a] dark:text-white font-light bg-white dark:bg-[#2a2a2a] px-1.5 py-0.5 border border-[#e8e6e3] dark:border-gray-700 shadow-sm">${d_3.avgSoldPricePerSqFt}</div>}<div className="w-full max-w-[24px] bg-[var(--color-navy)] dark:bg-[var(--color-gold)] transition-all duration-300 hover:opacity-80" style={{
          height: `${getBarHeight(d_3.avgSoldPricePerSqFt, maxPricePerSqFt)}%`
        }} /></div>;
      $[38] = maxPricePerSqFt;
      $[39] = t19;
    } else {
      t19 = $[39];
    }
    t18 = monthlyData.map(t19);
    $[35] = maxPricePerSqFt;
    $[36] = monthlyData;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[40] !== t18) {
    t19 = <div className="flex items-end justify-between h-full gap-1 relative z-0">{t18}</div>;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t17 || $[43] !== t19) {
    t20 = <div className="relative h-32">{t16}{t17}{t19}</div>;
    $[42] = t17;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== monthlyData) {
    t21 = monthlyData.map(_temp7);
    $[45] = monthlyData;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t21) {
    t22 = <div className="flex justify-between mt-3 px-0.5">{t21}</div>;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t20 || $[50] !== t22) {
    t23 = <div className="p-6 md:p-8">{t15}{t20}{t22}</div>;
    $[49] = t20;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t14 || $[53] !== t23) {
    t24 = <div className="grid grid-cols-1 md:grid-cols-2">{t14}{t23}</div>;
    $[52] = t14;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="flex items-center gap-2"><div className="w-3 h-3 bg-[var(--color-navy)] dark:bg-[var(--color-gold)]" /><span className="text-[10px] uppercase tracking-[0.1em] text-[#6a6a6a] dark:text-gray-400 font-light">Monthly Average</span></div>;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div className="px-6 py-4 border-t border-[#e8e6e3] dark:border-gray-800 flex items-center justify-center gap-8">{t25}<div className="flex items-center gap-2"><div className="w-6 h-px bg-[var(--color-gold)]" style={{
          backgroundImage: "repeating-linear-gradient(90deg, var(--color-gold) 0, var(--color-gold) 4px, transparent 4px, transparent 8px)"
        }} /><span className="text-[10px] uppercase tracking-[0.1em] text-[#6a6a6a] dark:text-gray-400 font-light">12-Month Average</span></div></div>;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== t24) {
    t27 = <div className="bg-white dark:bg-[#1a1a1a] border border-[#e8e6e3] dark:border-gray-800">{t5}{t24}{t26}</div>;
    $[57] = t24;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  return t27;
}
function _temp7(d_4, i_4) {
  return <span key={i_4} className="flex-1 text-center text-[9px] uppercase tracking-[0.05em] text-[#8a8a8a] font-light">{d_4.month}</span>;
}
function _temp6(i_2) {
  return <div key={i_2} className="w-full h-px bg-[#e8e6e3] dark:bg-gray-800" />;
}
function _temp5(d_2, i_1) {
  return <span key={i_1} className="flex-1 text-center text-[9px] uppercase tracking-[0.05em] text-[#8a8a8a] font-light">{d_2.month}</span>;
}
function _temp4(i) {
  return <div key={i} className="w-full h-px bg-[#e8e6e3] dark:bg-gray-800" />;
}
function _temp3(d_0) {
  return d_0.avgSoldPrice !== null;
}
function _temp2(avg, max_0) {
  if (!avg || max_0 === 0) {
    return 0;
  }
  return avg / max_0 * 100;
}
function _temp(value, max) {
  if (!value || max === 0) {
    return 0;
  }
  return Math.max(5, value / max * 100);
}